import React, { createContext, useCallback, useContext, useState } from 'react';
import { AlertLevel } from '../app/types';
import { ErrorMessage } from '../app/utils';

/**
 * AlertContextType defines the structure of the context used by the AlertProvider component.
 * It includes the current alert state, a function to set the alert, and a function to clear the alert.
 *
 * @typedef {Object} AlertContextType
 *
 * @property {{
 *   message: ErrorMessage;
 *   level: AlertLevel;
 *   title?: string;
 *   autoHideDuration?: number | null;
 * } | null} alert - The current alert object, or null if no alert is active.
 * The alert object contains:
 * - `message`: The error message associated with the alert.
 * - `level`: The severity level of the alert (e.g., 'error', 'warning', 'info').
 * - `title` (optional): A title for the alert.
 * - `autoHideDuration` (optional): The duration in milliseconds for which the alert should be displayed before automatically hiding.
 *
 * @property {(message: ErrorMessage, level?: AlertLevel, title?: string, autoHideDuration?: number | null) => void} setAlert -
 * A function to set the alert state. It accepts:
 * - `message`: The error message for the alert.
 * - `level` (optional): The severity level of the alert, defaulting to 'error'.
 * - `title` (optional): A title for the alert.
 * - `autoHideDuration` (optional): The duration in milliseconds for the alert.
 *
 * @property {() => void} clearAlert - A function to clear the current alert state by setting it to null.
 */
type AlertContextType = {
  alert: {
    message: ErrorMessage;
    level: AlertLevel;
    title?: string;
    autoHideDuration?: number | null;
  } | null;
  setAlert: (
    message: ErrorMessage,
    level?: AlertLevel,
    title?: string,
    autoHideDuration?: number | null,
  ) => void;
  clearAlert: () => void;
};

/**
 * AlertContext is a React context that provides a way to pass alert data down through the component tree without having to pass props down manually at every level.
 * It is created with a default value of undefined.
 * @type {React.Context<AlertContextType | undefined>}
 */
const AlertContext: React.Context<AlertContextType | undefined> = createContext<
  AlertContextType | undefined
>(undefined);

/**
 * AlertProviderProps is a type definition for the props of the AlertProvider component.
 * It has a single prop, children, which is of type ReactNode.
 * @typedef {Object} AlertProviderProps
 * @property {React.ReactNode} children - The child components of the AlertProvider.
 */
type AlertProviderProps = {
  children: React.ReactNode;
};

/**
 * AlertProvider is a React component that provides the AlertContext to its child components.
 * It maintains the alert state and provides functions to set and clear the alert.
 * @param {AlertProviderProps} props - The props of the AlertProvider component.
 * @returns {JSX.Element} A JSX element that provides the AlertContext to its child components.
 */
const AlertProvider = ({ children }: AlertProviderProps): React.JSX.Element => {
  const [alert, setAlertState] = useState<{
    message: ErrorMessage;
    level: AlertLevel;
    title?: string;
    autoHideDuration?: number | null;
  } | null>(null);

  /**
   * setAlert is a function that sets the alert state to a given message and level.
   * If no level is provided, it defaults to 'error'.
   * @param {string} message - The message of the alert.
   * @param {AlertLevel} [level='error'] - The level of the alert.
   * @param {string} [title] - An optional title for the alert.
   * @param {number} [autoHideDuration] - An optional duration in milliseconds for which the alert should be displayed before automatically hiding.
   */
  const setAlert = useCallback(
    (
      message: ErrorMessage,
      level: AlertLevel = 'error',
      title?: string,
      autoHideDuration?: number | null,
    ) => {
      setAlertState({ message, level, title, autoHideDuration });
    },
    [],
  );

  /**
   * clearAlert is a function that clears the alert state by setting it to null.
   */
  const clearAlert = () => setAlertState(null);

  return (
    <AlertContext.Provider value={{ alert, setAlert, clearAlert }}>
      {children}
    </AlertContext.Provider>
  );
};

/**
 * useAlert is a custom hook that provides the AlertContext.
 * It throws an error if it is used outside of an AlertProvider.
 * @returns {AlertContextType} The AlertContext.
 */
const useAlert = (): AlertContextType => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlert must be used within an AlertProvider');
  }
  return context;
};

export { AlertProvider, useAlert };
